<script>
import { mapActions, mapGetters } from 'vuex';
import { iSpinner, iFeaturedIconTypes } from '@inscribe/signal';
import RuleEngine from '@/utils/ruleEngine';
import DecisionEngineCard from '@/V2/decision-engine/DecisionEngineCard.vue';
import RuleSave from '@/V2/decision-engine/rules/RuleSave.vue';

export default {
  components: {
    DecisionEngineCard,
    iSpinner,
    RuleSave,
  },
  data() {
    return {
      displayLink:
        'https://help.inscribe.ai/en/articles/7852994-understanding-inscribe-fraud-signals',
      iFeaturedIconTypes,
      showNewRuleModal: false,
      newRule: null,
      ruleError: null,
      rulesToBeSaved: null,
      mode: '',
      isLoading: true,
    };
  },
  computed: {
    ...mapGetters(['getRules', 'getRulesSchema', 'isOrgAdmin']),
    detectors() {
      return this.getRules.filter(
        (r) => r.default && RuleEngine.isSimpleDetectorRule(r),
      );
    },
  },
  mounted() {
    if (this.isOrgAdmin) {
      this.getConfigRules().then(() => {
        this.isLoading = false;
      });
    }
  },
  methods: {
    ...mapGetters(['getRuleIndex']),
    ...mapActions({
      getConfigRules: 'getConfigRules',
    }),

    getRuleTitle(rule) {
      return RuleEngine.getRuleTitle(rule);
    },

    getRuleSubtitle(rule) {
      const map = {
        DETECTOR_ADOBE_FONTS:
          'Identifies and flags documents using fonts from the Adobe Fonts service that are unusual for the document type',
        DETECTOR_EDITED_WHILE_SCANNED:
          'Flags documents that show signs of being modified or edited during the scanning process',
        DETECTOR_EXIF_DATA_MODIFIED:
          'Detects metadata anomalies that suggest possible file manipulation',
        DETECTOR_QPDF:
          'Analyzes PDFs for alterations that are typical of QPDF library manipulations',
        DETECTOR_REJECTED_CUSTOMER:
          'Flags documents previously submitted by individuals or entities that have previously been rejected',
        DETECTOR_TEXT_LAYER_TEXT:
          'Identifies files with added text on top of images within PDFs',
        DETECTOR_AMOUNT_FORMAT_ANOMALY:
          'Flags document text with unusual numerical formats that may indicate fraudulent activity',
        DETECTOR_ANNOTATIONS:
          'Flags annotated documents that may manipulate or obscure information',
        DETECTOR_DOCSTORE_TEMPLATE:
          "Compares a document's mathematical representation with known templates from online document stores",
        DETECTOR_MASKING:
          'Identifies regions where text is hidden using annotations or solid color blocks in PDFs',
        DETECTOR_XRAY:
          'Compares the oldest and most recent versions of a PDF, flagging differences and highlighting them in an x-ray viewer',
        DETECTOR_SEEN_BEFORE_NOT_FRAUDULENT:
          'Detects if the exact same document has been uploaded more than once for the same organization',
        DETECTOR_FLATTENED_PDF:
          'Detects when a PDF has been flattened, merging all layers into one, potentially indicating data manipulation',
        DETECTOR_SOFTWARE_BLACKLIST:
          'Flags documents where consumer software has been used, indicating potential fraud',
        DETECTOR_BLACKLIST:
          'Flags documents matching names and addresses on a user-defined blocklist as potentially fraudulent',
        DETECTOR_COPYCAT_IMAGE:
          'Flags images as fraudulent if highly similar to others with different names or identities in the database',
        DETECTOR_COPYCAT_TEXT:
          'Flags documents with highly similar text to others associated with different identities in the database',
        DETECTOR_TRANSACTIONS_DO_NOT_BALANCE:
          'Flags bank statements where balances do not reconcile with transactions',
        DETECTOR_TEXT_ALIGNMENT:
          'Flags bank statements with text that is not aligned in key regions of the document',
        DETECTOR_SUMMARY_NOT_BALANCED:
          'Flags bank statements where the values in the summary section do not reconcile',
        DETECTOR_PAYSLIP_MISCALCULATION:
          'Flags payslips where there the deduction values do not reconcile with the totals',
        DETECTOR_REPEAT_ACCOUNT_NUMBER:
          'Flags documents where the account number is repeated under documents with a different identity',
        DETECTOR_HIGH_RISK_CUSTOMER_SEEN_BEFORE:
          'Flags documents previously submitted by customers associated with at least one high risk document',
        DETECTOR_REPEAT_BALANCES:
          'Flags bank statements where key amounts are repeated under documents with a different identity',
        DETECTOR_AI_GENERATED: 'Flags documents that are likely AI generated',
        DETECTOR_INCONSISTENT_EIN:
          'Flags tax forms/letters of type CP-575 and 147C where there is more than one EIN',
        DETECTOR_STRIPPED_METADATA:
          'Flags documents with empty metadata fields, which may indicate stripped metadata used to hide signs of editing',
        DETECTOR_PDF_STRUCTURE_ANOMALY:
          'Flags documents where the internal structure shows anomalies',
        DETECTOR_SUSPICIOUS_PDF_METADATA:
          'Flags documents with suspicious PDF metadata',
        DETECTOR_SHARPNESS_INCONSISTENCY:
          'Flags images with sharpness inconsistencies in the text which may indicate fraudulent edits',
      };

      return map[rule.name] || 'tbd - ' + rule.name;
    },

    onSwitchChange(params) {
      this.$emit('hide-tooltips');
      const foundRuleIdx = this.getRuleIndex()(params.item);
      if (foundRuleIdx > -1) {
        this.rulesToBeSaved = [
          ...this.getRules.slice(0, foundRuleIdx),
          { ...params.item, enabled: params.value },
          ...this.getRules.slice(foundRuleIdx + 1),
        ];
      }
    },
  },
};
</script>

<template>
  <div class="detectors-page d-flex flex-column i-gap-12">
    <div class="d-flex align-items-center">
      <div class="text flex-grow-1">
        <div class="i-text-heading-5 i-text-neutral-120 i-mb-2">
          Fraud Detectors
        </div>
        <div class="i-text-body-2 i-text-neutral-110 subheading-wrap">
          View and manage signals for this workspace.
          <a rel="noopener" class="i-link" :href="displayLink" target="_blank"
            >Learn more</a
          >
        </div>
      </div>
    </div>

    <!-- loading screen-->
    <div
      v-if="isLoading"
      class="d-flex i-my-5 w-100 align-items-center justify-content-center"
    >
      <i-spinner></i-spinner>
    </div>

    <div
      v-if="getRulesSchema.variables && !isLoading"
      class="rules-list items-list i-radius-2xl d-flex flex-column"
      data-testid="default-rules"
    >
      <decision-engine-card
        v-for="(rule, ruleIndex) in detectors"
        :key="`${ruleIndex}-${JSON.stringify(rule)}`"
        :title="getRuleTitle(rule)"
        :subtitle="getRuleSubtitle(rule)"
        :item="rule"
        icon="medium-alert-triangle"
        :icon-type="iFeaturedIconTypes.ERROR"
        tooltip-toggle-on="Disable Rule"
        tooltip-toggle-off="Enable Rule"
        tooltip-edit="Edit Rule"
        tooltip-delete="Delete Rule"
        :switch-visible="true"
        :switch-value="rule.enabled"
        :display-tools="false"
        :with-hover-state="false"
        @switch-change="onSwitchChange"
      ></decision-engine-card>
    </div>

    <!-- rules save -->
    <rule-save :new-rules-collection="rulesToBeSaved"></rule-save>
  </div>
</template>
